import { ref } from "vue";
import type { TreeDefinitionDto, TreeNodeDto } from "~/api";
import { apiClient } from "~/utils/apiClient";
import { useMyInfoStore } from "~/stores/MyInfoStore";

/**
 * Recursively builds a flat GUID → TreeNodeDto map from a tree node hierarchy.
 */
export function buildTreeNodeMap(
  nodes: TreeNodeDto[],
  map: Map<string, TreeNodeDto>,
) {
  for (const node of nodes) {
    map.set(node.guid, node);
    if (node.children?.length) {
      buildTreeNodeMap(node.children, map);
    }
  }
}

const geoTreeDefinitionGuid = ref<string>("");
const geoTreeData = ref<TreeDefinitionDto | null>(null);
const flatNodeMap = ref(new Map<string, TreeNodeDto>());
const loading = ref(false);
let loaded = false;
let loadPromise: Promise<void> | null = null;

/**
 * Composable for loading the GEO_TREE definition and building
 * a flat GUID → TreeNodeDto lookup map.
 *
 * Cached at module level — the first call fetches from the server,
 * subsequent calls return the cached data immediately.
 */
export function useGeoTree() {
  const myInfoStore = useMyInfoStore();

  const loadGeoTree = async () => {
    if (loaded) return;
    if (loadPromise) return loadPromise;

    loadPromise = (async () => {
      loading.value = true;
      try {
        await myInfoStore.load();
        geoTreeData.value = await apiClient.tree.getGeoTreePublic(
          myInfoStore.currentSysOrgGuid,
        );
        geoTreeDefinitionGuid.value = geoTreeData.value.guid;
        flatNodeMap.value.clear();
        buildTreeNodeMap(geoTreeData.value.nodes, flatNodeMap.value);
        loaded = true;
      } catch {
        // No geo tree configured for this org
      } finally {
        loading.value = false;
        loadPromise = null;
      }
    })();

    return loadPromise;
  };

  const getNodeName = (guid: string): string | undefined =>
    flatNodeMap.value.get(guid)?.name;

  return {
    geoTreeDefinitionGuid,
    geoTreeData,
    flatNodeMap,
    loading,
    loadGeoTree,
    getNodeName,
  };
}
